import React, { useEffect, useState } from "react";
import { CustomerViewDetailsViewCampaignsInfoProps } from "./customer-view-details-view-campaigns-info.props";
import "./customer-view-details-view-campaigns-info.scss";
import AddToCampaignModal from "../../../../lead-actions/add-new-campaign/add-new-campiagn";
import CampaignTable from "../../../../../../common/components/modal-table/modal-table";
import {
  deleteCampaignAssign,
  editCampaignStatus,
  getCustomerDetails,
} from "../../../../../../api/customer";
import { useLogin } from "../../../../../../common/hooks/useUser";
import DeleteDialog from "./delete-dialog/delete-assign-campiagn";
import StatusDialog from "./status-dialog/update-status-dialog";

const CustomerViewDetailsViewCampaignsInfo: React.FC<
  CustomerViewDetailsViewCampaignsInfoProps
> = (props: any) => {
  const {
    allCustomerData,
    setLoading,
    setAlertMessage,
    setAlertType,
    setAlertOpen,
    disableEdit,
  } = props;

  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [campiagnDetails, setCampaignDetails] = useState<any>(null);
  const [customerName, setCustomerName] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<{
    customerId: number;
    associationId: number;
    name: string; // Change the type to ensure `name` is always a string
  } | null>(null);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [selectedCampaignIdForStatusEdit, setSelectedCampaignIdForStatusEdit] =
    useState<number | null>(null);
  const [
    selectedResourceTypeForStatusEdit,
    setSelectedResourceTypeForStatusEdit,
  ] = useState<"Lead" | "Customer">("Customer");

  const fetchCustomerDetails = async () => {
    setLoading(true);
    try {
      const response = await getCustomerDetails(allCustomerData);
      const customerData = response?.data?.data;
      setCustomerName(
        `${customerData?.first_name || ""} ${customerData?.middle_name || ""} ${customerData?.last_name || ""}`,
      );
      setCampaignDetails(
        (response?.data?.data?.campaigns || []).map((item: any) => ({
          ...item,
          start_date: item?.startDate,
          end_date: item?.endDate,
          membership_status: item.memberStatus,
          campaign_customer_association_id: item.campaignAssociationId,
          campaign_association_id: item.campaignAssociationId,
        })),
      );
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message ||
          "An error occurred while saving the lead.",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCustomerDetails();
  }, [allCustomerData]);

  // Handle delete campaign
  const handleDeleteCampaign = (campaignId: number, associationId: number) => {
    const selected = campiagnDetails?.find(
      (item: any) => item.id === campaignId,
    );
    const campaignName = selected?.name || ""; // Provide a default empty string if undefined
    setCampaignToDelete({
      customerId: allCustomerData,
      associationId,
      name: campaignName, // name will always be a string
    });
    setOpenDeleteDialog(true);
  };

  const confirmDeleteCampaign = async () => {
    if (!campaignToDelete) return;
    setLoading(true);
    try {
      await deleteCampaignAssign(
        campaignToDelete.customerId,
        campaignToDelete.associationId,
        "Customer Profile",
        selectedResourceTypeForStatusEdit,
      );
      await fetchCustomerDetails();
      setAlertMessage("Campaign unassigned successfully.");
      setAlertType("success");
      setAlertOpen(true);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to unassign campaign.",
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setOpenDeleteDialog(false);
      setCampaignToDelete(null);
      setLoading(false);
    }
  };
  // Handle status edit
  const handleEditStatus = (campaign: any) => {
    setStatusFilter(campaign.membership_status || campaign.status);
    setSelectedCampaignIdForStatusEdit(campaign.campaign_association_id);
    setSelectedResourceTypeForStatusEdit(campaign.resourceType);
    setOpenStatusDialog(true);
  };

  // Save status change
  const handleSaveStatus = async () => {
    if (selectedCampaignIdForStatusEdit != null) {
      setLoading(true);
      try {
        await editCampaignStatus(
          statusFilter,
          [selectedCampaignIdForStatusEdit],
          selectedResourceTypeForStatusEdit!,
          "Customer Profile",
        );
        await fetchCustomerDetails();
        setAlertMessage("Campaign Status Updated Successfully.");
        setAlertType("success");
        setAlertOpen(true);
      } catch (error: any) {
        setAlertMessage(
          error?.response?.data?.message || "Failed to update campaign status.",
        );
        setAlertType("error");
        setAlertOpen(true);
      } finally {
        setLoading(false);
        setOpenStatusDialog(false);
        setSelectedCampaignIdForStatusEdit(null);
      }
    }
  };

  return (
    <div className="customer-view-details-view-campaigns-info">
      <div className="customer-view-details-view-campaigns-info-header">
        <div className="customer-view-details-view-campaigns-info-header-text">
          Campaigns
        </div>
        <div>
          <button
            className="customer-view-details-view-campaigns-info-header-button"
            onClick={() => setIsCampaignModalOpen(true)}
            disabled={disableEdit}
          >
            Add to Campaigns
          </button>
        </div>
      </div>

      <div className="customer-view-details-view-campaigns-info-no-data">
        {campiagnDetails && campiagnDetails.length > 0 ? (
          <CampaignTable
            data={campiagnDetails}
            viewCampaign={true}
            onDelete={handleDeleteCampaign}
            onEditStatus={handleEditStatus}
            setResourceType={setSelectedResourceTypeForStatusEdit}
          />
        ) : (
          <div className="no-record-text">No Record Found</div>
        )}
      </div>

      <AddToCampaignModal
        customerIds={[allCustomerData]}
        isOpen={isCampaignModalOpen}
        setMenuItems={() => {}}
        selectedActions={"addToCampaign"}
        handleMassActionMenuClose={() => setIsCampaignModalOpen(false)}
        multipleCampaign={true}
        onSave={() => {
          fetchCustomerDetails();
          setIsCampaignModalOpen(false);
        }}
      />
      {/* Use DeleteDialog component */}
      <DeleteDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        campaignToDelete={campaignToDelete}
        customerName={customerName}
        confirmDeleteCampaign={confirmDeleteCampaign}
      />

      {/* Use StatusDialog component */}
      <StatusDialog
        open={openStatusDialog}
        onClose={() => setOpenStatusDialog(false)}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        handleSaveStatus={handleSaveStatus}
      />
    </div>
  );
};

export default CustomerViewDetailsViewCampaignsInfo;
